import { PageShell, type PageShellProps } from '@repo/ui/components/layout/page-shell'
import { Button } from '@repo/ui/components/ui/button'
import { cn } from '@repo/ui/lib/utils'
import { Maximize2, Minimize2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'

type ExpandablePageShellRenderProps = {
  expanded: boolean
}

export type ExpandablePageShellProps = Omit<PageShellProps, 'children' | 'width'> & {
  children: ReactNode | ((props: ExpandablePageShellRenderProps) => ReactNode)
  expandedWidth?: PageShellProps['width']
  collapsedWidth?: PageShellProps['width']
}

export function ExpandablePageShell({
  children,
  expandedWidth = 'full',
  collapsedWidth = 'wide',
  density = 'comfortable',
  contentSize = 'lg',
  className,
  contentClassName,
  ...pageShellProps
}: ExpandablePageShellProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <PageShell
      {...pageShellProps}
      density={density}
      contentSize={contentSize}
      width={expanded ? expandedWidth : collapsedWidth}
      className={cn('h-[var(--app-shell-body-height)] min-h-0', className)}
      contentClassName={cn('relative h-full min-h-0 w-full', contentClassName)}
    >
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={expanded ? 'Collapse workspace' : 'Expand workspace'}
        className="absolute top-0 -right-8 z-10 hidden size-7 rounded-[5px] bg-background/95 backdrop-blur 2xl:inline-flex"
        onClick={() => setExpanded((current) => !current)}
      >
        {expanded ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
      </Button>
      <div className="h-full min-h-0 w-full">
        {typeof children === 'function' ? children({ expanded }) : children}
      </div>
    </PageShell>
  )
}
